import axios from 'axios';
import { getServiceApiUrl } from '../utils/constants';
import { dashboardRangeParamsForService } from '../utils/reportingPeriod';
import { logDataAccess, logHipaaAudit } from './activityLogService';
import { attachAxiosAccessRefresh } from '@saas-foundation/shared-frontend/utils/sessionKeepAlive';

// Base API client
const createApiClient = (baseURL) => {
  const client = axios.create({
    baseURL,
    timeout: 3000, // 3 second timeout for faster failure detection (was 5s)
    headers: {
      'Content-Type': 'application/json',
    },
  });

  attachAxiosAccessRefresh(client);

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      // Add auth token if available
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add organization_id to all requests if available
      const organizationId = localStorage.getItem('selectedOrganizationId') || 
                             localStorage.getItem('organizationId');
      if (organizationId) {
        // Merge with existing params
        config.params = {
          ...config.params,
          organization_id: organizationId
        };
      }
      
      // Store request metadata for logging
      config.metadata = {
        url: config.url,
        method: config.method,
        timestamp: Date.now(),
      };
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => {
      // Log data access for HIPAA compliance
      const config = response.config;
      const token = localStorage.getItem('token');
      const organizationId = localStorage.getItem('selectedOrganizationId') || localStorage.getItem('organizationId');
      const userStr = localStorage.getItem('user');
      
      if (config.metadata && token && organizationId && userStr) {
        try {
          const user = JSON.parse(userStr);
          const url = config.metadata.url || '';
          const method = config.metadata.method || 'GET';
          
          // Determine resource type from URL
          let resource = 'unknown';
          let action = 'view';
          
          if (url.includes('/denials')) {
            resource = 'denial';
            action = 'list';
          } else if (url.includes('/claims')) {
            resource = 'claim';
            action = 'list';
          } else if (url.includes('/charges')) {
            resource = 'charge';
            action = 'list';
          } else if (url.includes('/insurance')) {
            resource = 'insurance_verification';
            action = 'list';
          } else if (url.includes('/prior-auth')) {
            resource = 'prior_authorization';
            action = 'list';
          }
          
          // Log HIPAA audit for PHI access (async, non-blocking)
          // Use setTimeout to make it truly non-blocking
          setTimeout(() => {
          if (['denial', 'claim', 'charge', 'insurance_verification', 'prior_authorization'].includes(resource)) {
            logHipaaAudit(action, resource, null, user, organizationId, token, {
              url,
              method,
              response_status: response.status,
            }).catch(err => console.warn('Failed to log HIPAA audit:', err));
          } else {
            // Log general data access
            logDataAccess(resource, action, user, organizationId, token, {
              url,
              method,
            }).catch(err => console.warn('Failed to log data access:', err));
          }
          }, 0);
        } catch (err) {
          console.warn('Error logging data access:', err);
        }
      }
      
      return response.data;
    },
    (error) => {
      // Handle 404 errors gracefully - return empty array instead of rejecting (silently)
      if (error.response?.status === 404) {
        // Don't log 404s - they're expected for missing endpoints
        return [];
      }
      // Handle timeouts gracefully (silently)
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('exceeded')) {
        return [];
      }
      // For other errors, log but don't break the dashboard
      if (error.response?.status >= 500) {
        console.error('API Server Error:', error.response?.status, error.config?.url);
        return [];
      }
      // For 4xx errors (except 404), still return empty array to prevent dashboard breakage
      if (error.response?.status >= 400) {
        // Only log non-404 errors (404s are expected when endpoints don't exist)
        if (error.response?.status !== 404) {
          console.warn(`API Client Error: ${error.response?.status} - ${error.config?.url}`);
        }
        return [];
      }
      // Silently handle network errors (ERR_NETWORK, ERR_CONNECTION_REFUSED, ERR_CONNECTION_RESET) - these are expected when services aren't running
      const isNetworkError = error.code === 'ERR_NETWORK' || 
                            error.code === 'ERR_CONNECTION_REFUSED' ||
                            error.code === 'ERR_CONNECTION_RESET' ||
                            error.message?.includes('Network Error') ||
                            error.message?.includes('Connection refused') ||
                            error.message?.includes('Connection reset') ||
                            error.message?.includes('ECONNRESET') ||
                            error.message?.includes('timeout') ||
                            error.message?.includes('exceeded');
      
      // Only log unexpected errors (not network errors or timeouts)
      if (!isNetworkError && error.message) {
        console.error('API Error:', error);
      }
      return [];
    }
  );

  return client;
};

// Service-specific clients
export const denialService = createApiClient(getServiceApiUrl('DENIAL_MANAGEMENT'));
export const claimsService = createApiClient(getServiceApiUrl('CLAIMS_PROCESSING'));
const chargeCaptureBaseUrl = getServiceApiUrl('CHARGE_CAPTURE');
export const chargeService = chargeCaptureBaseUrl ? createApiClient(chargeCaptureBaseUrl) : null;
export const insuranceService = createApiClient(getServiceApiUrl('INSURANCE_VERIFICATION'));
export const priorAuthService = createApiClient(getServiceApiUrl('PRIOR_AUTHORIZATION'));
const arPostingBaseUrl = getServiceApiUrl('AR_POSTING');
export const arPostingService = arPostingBaseUrl ? createApiClient(arPostingBaseUrl) : null;
const ediPlatformBaseUrl = getServiceApiUrl('EDI_PLATFORM');
export const clearinghouseService = ediPlatformBaseUrl ? createApiClient(ediPlatformBaseUrl) : null;

// Demo/mock dashboard data is opt-in only (e.g. sales demos). Default: show real API data or empty states.
const USE_DASHBOARD_MOCK = process.env.REACT_APP_DASHBOARD_USE_MOCK === 'true';

const EDI_DASHBOARD_API_KEY = process.env.REACT_APP_EDI_API_KEY || 'edi_test_api_key_12345';

const DASHBOARD_SERVICE_ENV_KEYS = {
  DenialManagement: 'DENIAL_MANAGEMENT',
  'DenialManagement WorkQueue': 'DENIAL_MANAGEMENT',
  ClaimsProcessing: 'CLAIMS_PROCESSING',
  'ClaimsProcessing WorkQueue': 'CLAIMS_PROCESSING',
  ChargeCapture: 'CHARGE_CAPTURE',
  'ChargeCapture WorkQueue': 'CHARGE_CAPTURE',
  InsuranceVerification: 'INSURANCE_VERIFICATION',
  'InsuranceVerification WorkQueue': 'INSURANCE_VERIFICATION',
  PriorAuthorization: 'PRIOR_AUTHORIZATION',
  'PriorAuthorization WorkQueue': 'PRIOR_AUTHORIZATION',
  ARPosting: 'AR_POSTING',
  'ARPosting WorkQueue': 'AR_POSTING',
  'ARPosting Reconciliation': 'AR_POSTING',
  ClearingHouse: 'EDI_PLATFORM',
};

const resolveDashboardServiceBase = (service, serviceLabel) => {
  const envKey = DASHBOARD_SERVICE_ENV_KEYS[serviceLabel];
  if (envKey) {
    const resolved = getServiceApiUrl(envKey);
    if (resolved) return String(resolved).replace(/\/$/, '');
  }
  const fallback = service?.defaults?.baseURL;
  return typeof fallback === 'string' ? fallback.replace(/\/$/, '') : null;
};

// Per-service dashboard timeouts (ms). Denial Management calls Core Claims HOT and needs longer.
const DASHBOARD_TIMEOUT_MS = {
  DenialManagement: 20000,
  'DenialManagement WorkQueue': 20000,
  ClearingHouse: 4000,
  default: 10000,
};

const getDashboardAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const classifyDashboardError = (err, url, durationMs, serviceLabel) => {
  const isDenial = serviceLabel.includes('Denial');
  if (err?.code === 'ECONNABORTED' || String(err?.message || '').includes('timeout')) {
    return {
      status: 'timeout',
      url,
      durationMs,
      detail: err.message,
      hint: isDenial
        ? 'Denial Management loads claims from Core Claims (port 8102). Ensure Claims + Denial backends are running; first load can take 10–20s.'
        : 'Request timed out — service may be slow or unreachable.',
    };
  }
  if (err?.code === 'ERR_NETWORK' || err?.code === 'ERR_CONNECTION_REFUSED' || err?.code === 'ERR_CONNECTION_RESET') {
    return {
      status: 'network',
      url,
      durationMs,
      detail: err.message,
      hint: `Cannot reach ${url}. Verify the service is running and the port is open from your browser host.`,
    };
  }
  const httpStatus = err?.response?.status;
  if (httpStatus) {
    const detail = err?.response?.data?.detail || err?.response?.data?.message || err.message;
    return {
      status: `http_${httpStatus}`,
      url,
      durationMs,
      detail: typeof detail === 'object' ? JSON.stringify(detail) : detail,
      hint: serviceLabel === 'ClearingHouse' && httpStatus === 502
        ? 'EDI Platform is down or nginx cannot reach edi_platform_backend:8000. Clearinghouse tiles are optional; other dashboard sources still load.'
        : httpStatus >= 500
        ? 'Server error — check backend logs (often Core Claims / HOT fetch or database).'
        : `HTTP ${httpStatus} from service.`,
    };
  }
  return {
    status: 'error',
    url,
    durationMs,
    detail: err?.message || 'Unknown error',
    hint: 'Unexpected request failure.',
  };
};

const isDashboardPayloadValid = (data, serviceName) => {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }
  if (serviceName === 'DenialManagement') {
    return Boolean(data.denial_summary || data.financial_summary);
  }
  if (serviceName === 'ARPosting') {
    const flat = data.summary || data;
    return Boolean(
      flat.total_posted_today != null
      || flat.total_postings_count != null
      || flat.auto_posting_rate != null
      || flat.total_amount_posted != null
    );
  }
  if (serviceName === 'ClearingHouse') {
    return data.total_transactions != null || data.success_rate != null;
  }
  if (serviceName === 'ChargeCapture') {
    return Boolean(data.summary && typeof data.summary === 'object');
  }
  if (serviceName === 'ClaimsProcessing') {
    return Boolean(
      data.financial_summary
      || data.processing_summary
      || data.recent_claims_30_days != null
      || data.cohort?.loaded_claims != null
    );
  }
  if (serviceName.includes('WorkQueue')) {
    return Object.keys(data).length > 0;
  }
  return Object.keys(data).length > 0;
};

/** AR Posting /dashboard/summary returns flat KPI fields; shell expects { summary: {...} }. */
const normalizeArPostingDashboardStats = (data, reconciliation = null) => {
  if (!data || typeof data !== 'object') return null;
  const flat = data.summary && typeof data.summary === 'object' ? data.summary : data;
  const pendingPostings = flat.pending_postings ?? flat.pending_actions ?? 0;
  const pendingApprovals = flat.pending_approvals ?? 0;
  return {
    ...data,
    summary: {
      total_posted_today: flat.total_posted_today ?? 0,
      total_posted: flat.total_postings_count ?? flat.total_posted ?? 0,
      total_postings: flat.total_postings_count ?? flat.total_posted ?? 0,
      total_amount_posted: flat.total_amount_posted ?? 0,
      auto_posting_rate: flat.auto_posting_rate ?? 0,
      reconciliation_rate:
        reconciliation?.reconciliation_rate ?? flat.reconciliation_rate ?? 0,
      pending_actions: flat.pending_actions ?? pendingPostings + pendingApprovals,
      pending_postings: pendingPostings,
      unreconciled_deposits: flat.unreconciled_deposits ?? 0,
    },
  };
};

const getDistributionCount = (distribution, key) => {
  const entry = distribution?.[key];
  if (typeof entry === 'number') return entry;
  if (entry && typeof entry === 'object') return entry.count ?? 0;
  return 0;
};

/** Map Claims Processing /dashboard/work-queue-status (story_7) to operational dashboard shape. */
const normalizeClaimsWorkQueueStructure = (data) => {
  if (!data || typeof data !== 'object') return null;

  if (
    data.total_items != null
    && data.pending_claims == null
    && data.unassigned_count == null
  ) {
    return {
      total_items: data.total_items || 0,
      pending_count: data.pending_count || 0,
      in_progress_count: data.in_progress_count || 0,
      completed_today: data.completed_today || 0,
      assigned_to_ai: data.assigned_to_ai || 0,
      assigned_to_human: data.assigned_to_human || 0,
      overdue_count: data.overdue_count || 0,
      avg_processing_time: data.avg_processing_time || 0,
      avg_age_days: data.avg_age_days,
    };
  }

  const pendingClaims = Number(data.pending_claims ?? data.pending_count ?? 0);
  const statusDist = data.status_distribution || {};
  const inProgress =
    getDistributionCount(statusDist, 'validating')
    + getDistributionCount(statusDist, 'queued');
  const wqAnalysis = data.ai_contribution?.work_queue_analysis;
  const aiPending = Number(wqAnalysis?.ai_pending_count ?? 0);
  const humanPending = Number(
    wqAnalysis?.human_pending_count ?? Math.max(0, pendingClaims - aiPending)
  );

  return {
    total_items: pendingClaims,
    pending_count: pendingClaims,
    in_progress_count: inProgress,
    completed_today: Number(data.completed_today ?? 0),
    assigned_to_ai: aiPending,
    assigned_to_human: humanPending,
    overdue_count: Number(data.approaching_deadline_count ?? data.overdue_count ?? 0),
    avg_processing_time: Number(data.avg_age_days ?? data.avg_processing_time ?? 0),
    avg_age_days: Number(data.avg_age_days ?? 0),
  };
};

/** Claims /v1/dashboard/stats and /reports/dashboard; UI expects processing_summary fields. */
const normalizeClaimsDashboardStats = (data, workQueue = null) => {
  if (!data || typeof data !== 'object') return null;
  const summary = {
    ...(data.processing_summary && typeof data.processing_summary === 'object' ? data.processing_summary : {}),
  };
  const recent30 = data.recent_claims_30_days ?? summary.total_processed ?? null;

  if (summary.success_rate == null && data.success_rate != null) {
    summary.success_rate = data.success_rate;
  } else if (summary.success_rate == null && summary.total_processed > 0) {
    const rejected = Number(summary.rejected_claims ?? 0);
    const total = Number(summary.total_processed);
    if (total > 0) {
      summary.success_rate = Number(((Math.max(0, total - rejected) / total) * 100).toFixed(1));
    }
  }

  if (summary.total_processed == null) {
    summary.total_processed =
      recent30
      ?? data.recent_claims_90_days
      ?? data.hot_stats?.total_claims
      ?? null;
  }

  if (summary.automated_processing == null) {
    summary.automated_processing =
      summary.ai_processed
      ?? workQueue?.assigned_to_ai
      ?? null;
  }

  if (summary.pending_claims == null && workQueue?.pending_count != null) {
    summary.pending_claims = workQueue.pending_count;
  }

  return {
    ...data,
    recent_claims_30_days: recent30 ?? data.recent_claims_30_days,
    processing_summary: summary,
  };
};

/** Denials mock/historical payloads may omit performance_metrics.appeal_success_rate. */
const normalizeDenialsDashboardStats = (data) => {
  if (!data || typeof data !== 'object') return null;
  const perf = { ...(data.performance_metrics || {}) };
  const fin = { ...(data.financial_summary || {}) };
  const funnel = data.outcome_funnel_summary || {};

  if ((fin.total_recovered ?? 0) === 0 && Number(funnel.recovered_value ?? 0) > 0) {
    const recovered = Number(funnel.recovered_value);
    fin.total_recovered = recovered;
    fin.recovered_amount = recovered;
  }

  if (perf.appeal_success_rate == null && data.workflow_summary) {
    const ws = data.workflow_summary;
    const wins = Number(ws.successful_appeals ?? 0);
    const losses = Number(ws.failed_appeals ?? 0);
    const total = wins + losses;
    if (total > 0) {
      perf.appeal_success_rate = Number(((wins / total) * 100).toFixed(1));
    }
  }

  if ((perf.appeal_success_rate ?? 0) === 0 && Number(funnel.recovery_rate ?? 0) > 0) {
    perf.appeal_success_rate = Number(funnel.recovery_rate);
  }
  if (perf.total_appeals == null && Number(funnel.paid_recovered_count ?? 0) > 0) {
    perf.total_appeals = Number(funnel.paid_recovered_count) + Number(funnel.permanently_denied_count ?? 0);
  }

  return { ...data, financial_summary: fin, performance_metrics: perf };
};


const SERVICE_SOURCE_KEYS = {
  DenialManagement: 'denials',
  'DenialManagement WorkQueue': 'denialsWorkQueue',
  ClaimsProcessing: 'claims',
  'ClaimsProcessing WorkQueue': 'claimsWorkQueue',
  ChargeCapture: 'charges',
  'ChargeCapture WorkQueue': 'chargesWorkQueue',
  InsuranceVerification: 'insurance',
  'InsuranceVerification WorkQueue': 'insuranceWorkQueue',
  PriorAuthorization: 'priorAuth',
  'PriorAuthorization WorkQueue': 'priorAuthWorkQueue',
  ARPosting: 'arPosting',
  'ARPosting WorkQueue': 'arPostingWorkQueue',
  ClearingHouse: 'clearinghouse',
};

const createEmptySources = () => ({
  denials: 'unavailable',
  claims: 'unavailable',
  charges: 'unavailable',
  insurance: 'unavailable',
  priorAuth: 'unavailable',
  arPosting: 'unavailable',
  clearinghouse: 'unavailable',
  denialsWorkQueue: 'unavailable',
  claimsWorkQueue: 'unavailable',
  chargesWorkQueue: 'unavailable',
  insuranceWorkQueue: 'unavailable',
  priorAuthWorkQueue: 'unavailable',
  arPostingWorkQueue: 'unavailable',
  'kpis.daysInAR': 'unavailable',
  'kpis.netCollectionRate': 'unavailable',
  'kpis.posCollections': 'unavailable',
  'kpis.propensityToPay': 'unavailable',
  'kpis.costToCollect': 'unavailable',
  'kpis.automationRate': 'unavailable',
});

const summarizeSources = (sources) => {
  const values = Object.values(sources);
  const liveCount = values.filter((v) => v === 'live').length;
  const mockCount = values.filter((v) => v === 'mock').length;
  const unavailableCount = values.filter((v) => v === 'unavailable').length;
  return {
    liveCount,
    mockCount,
    unavailableCount,
    useMock: mockCount > 0,
    allUnavailable: liveCount === 0 && mockCount === 0,
    allLive: unavailableCount === 0 && mockCount === 0,
  };
};

// Helper function to get organization_id (synchronous from localStorage)
const getOrganizationId = () => {
  // Try to get from localStorage first (fastest)
  const storedOrg = localStorage.getItem('selectedOrganizationId') || 
                   localStorage.getItem('organizationId');
  if (storedOrg) {
    return storedOrg;
  }
  
  // Try to get from user object in localStorage
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      const orgId = user?.organization_id || user?.organizationId;
      if (orgId) {
        localStorage.setItem('selectedOrganizationId', orgId);
        localStorage.setItem('organizationId', orgId);
        return orgId;
      }
    }
  } catch (error) {
    console.warn('Could not parse user from localStorage:', error);
  }
  
  return null;
};

// Unified RCM Service
export const rcmService = {
  // Dashboard data - Optimized to fetch Executive Summary KPIs from each service
  async getDashboardData(organizationIdOverride, reportingPeriod) {
    try {
      // Get organization_id (synchronous); allow explicit override from Agency/executive callers
      const organizationId = organizationIdOverride || getOrganizationId();
      
      console.log('[Dashboard] Fetching executive summary KPIs, organizationId:', organizationId);
      
      // If no organization_id, return empty data (will be handled gracefully by UI)
      if (!organizationId) {
        console.warn('[Dashboard] No organization_id available, returning empty dashboard data');
        return this.getEmptyDashboardData('no_organization');
      }

      const diagnostics = {};

      // Dashboard requests bypass axios interceptors (which swallow errors as []) so we get real failure reasons.
      const createStatsRequest = (service, endpoint, serviceLabel, timeoutMs, headers = {}) => {
        const sourceKey = SERVICE_SOURCE_KEYS[serviceLabel];
        const diagnosticKey = sourceKey || serviceLabel;
        const effectiveTimeout = timeoutMs ?? DASHBOARD_TIMEOUT_MS[serviceLabel] ?? DASHBOARD_TIMEOUT_MS.default;
        const baseURL = resolveDashboardServiceBase(service, serviceLabel);
        const serviceRangeParams = dashboardRangeParamsForService(serviceLabel, reportingPeriod);

        if (!baseURL) {
          const diagnostic = {
            status: 'skipped',
            url: null,
            durationMs: 0,
            detail: 'No API client configured',
            hint: serviceLabel.includes('Denial')
              ? 'Set REACT_APP_DENIAL_MANAGEMENT_API_URL or REACT_APP_USE_DEFAULT_RCM_PORTS=true for remote hosts.'
              : serviceLabel === 'ClearingHouse'
                ? 'EDI Platform URL not configured — shell should use /rcm-api/edi-platform on port 3160.'
                : 'Service URL not configured — check REACT_APP_* env vars.',
          };
          diagnostics[diagnosticKey] = diagnostic;
          return Promise.resolve({ data: null, diagnostic });
        }

        const fullUrl = `${baseURL}${endpoint}`;
        const started = Date.now();

        return axios.get(fullUrl, {
          params: { organization_id: organizationId, ...serviceRangeParams },
          timeout: effectiveTimeout,
          headers: {
            'Content-Type': 'application/json',
            ...getDashboardAuthHeaders(),
            ...headers,
          },
        })
          .then((response) => {
            const durationMs = Date.now() - started;
            const data = response.data;
            if (!isDashboardPayloadValid(data, serviceLabel)) {
              const diagnostic = {
                status: 'empty',
                url: fullUrl,
                durationMs,
                detail: Array.isArray(data) ? 'empty array response' : 'missing expected fields',
                hint: 'Endpoint responded but payload was empty or unexpected shape.',
              };
              diagnostics[diagnosticKey] = diagnostic;
              return { data: null, diagnostic };
            }
            const diagnostic = { status: 'ok', url: fullUrl, durationMs };
            diagnostics[diagnosticKey] = diagnostic;
            return { data, diagnostic };
          })
          .catch((err) => {
            const diagnostic = classifyDashboardError(err, fullUrl, Date.now() - started, serviceLabel);
            diagnostics[diagnosticKey] = diagnostic;
            return { data: null, diagnostic };
          });
      };
      
      const startTime = Date.now();
      const [
        denialStats, claimsStats, chargeStats, insuranceStats, priorAuthStats, arPostingStats, arReconciliationStats, clearinghouseStats,
        denialWorkQueue, claimsWorkQueue, chargeWorkQueue, insuranceWorkQueue, priorAuthWorkQueue, arPostingWorkQueue,
        daysInAR, netCollectionRate, posCollections, propensityToPay, costToCollect
      ] = await Promise.allSettled([
        createStatsRequest(denialService, '/api/v1/dashboard/stats', 'DenialManagement'),
        createStatsRequest(claimsService, '/api/v1/dashboard/stats', 'ClaimsProcessing'),
        createStatsRequest(chargeService, '/api/v1/dashboard/summary', 'ChargeCapture'),
        createStatsRequest(insuranceService, '/api/v1/dashboard/summary', 'InsuranceVerification'),
        createStatsRequest(priorAuthService, '/api/v1/dashboard/approval-rate', 'PriorAuthorization'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/summary', 'ARPosting'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/reconciliation-status', 'ARPosting Reconciliation'),
        createStatsRequest(clearinghouseService, '/api/v1/monitoring/metrics', 'ClearingHouse', null, { 'X-API-Key': EDI_DASHBOARD_API_KEY }),
        createStatsRequest(denialService, '/api/v1/dashboard/work-queue-status', 'DenialManagement WorkQueue'),
        createStatsRequest(claimsService, '/api/v1/dashboard/work-queue-status', 'ClaimsProcessing WorkQueue'),
        createStatsRequest(chargeService, '/api/v1/dashboard/work-queue-status', 'ChargeCapture WorkQueue'),
        createStatsRequest(insuranceService, '/api/v1/dashboard/work-queue-status', 'InsuranceVerification WorkQueue'),
        createStatsRequest(priorAuthService, '/api/v1/dashboard/work-queue-status', 'PriorAuthorization WorkQueue'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/work-queue-status', 'ARPosting WorkQueue'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/kpis/days-in-ar', 'kpis.daysInAR'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/kpis/net-collection-rate', 'kpis.netCollectionRate'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/kpis/pos-collections', 'kpis.posCollections'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/kpis/propensity-to-pay', 'kpis.propensityToPay'),
        createStatsRequest(arPostingService, '/api/v1/dashboard/kpis/cost-to-collect', 'kpis.costToCollect'),
      ]);
      
      const loadTime = Date.now() - startTime;
      console.log(`[Dashboard] Stats loaded in ${loadTime}ms`);

      const unavailable = Object.entries(diagnostics).filter(([, d]) => d.status !== 'ok');
      if (unavailable.length > 0) {
        console.warn('[Dashboard] Unavailable sources:', Object.fromEntries(unavailable));
      }

      const sources = createEmptySources();

      const getPayload = (result) => {
        if (result.status !== 'fulfilled' || result.value == null) {
          return { data: null, diagnostic: { status: 'error', detail: result.reason?.message || 'request failed' } };
        }
        return result.value;
      };

      const getMockFallback = (serviceName) => {
        const mockData = rcmService.getMockData(serviceName);
        if (serviceName.includes('WorkQueue')) {
          if (mockData) {
            return rcmService.ensureWorkQueueStructure(mockData);
          }
          return rcmService.getDefaultWorkQueueData();
        }
        return mockData || null;
      };

      const markSource = (serviceName, source) => {
        const key = SERVICE_SOURCE_KEYS[serviceName];
        if (key) {
          sources[key] = source;
        }
      };

      // Extract data from Promise.allSettled results — real API data only unless mock is enabled
      const extractStats = (result, serviceName) => {
        const sourceKey = SERVICE_SOURCE_KEYS[serviceName];
        const { data, diagnostic } = getPayload(result);

        if (data != null && isDashboardPayloadValid(data, serviceName)) {
          if (serviceName.includes('WorkQueue')) {
            markSource(serviceName, 'live');
            if (serviceName === 'ClaimsProcessing WorkQueue') {
              return normalizeClaimsWorkQueueStructure(data);
            }
            return rcmService.ensureWorkQueueStructure(data);
          }
          if (serviceName === 'ARPosting') {
            markSource(serviceName, 'live');
            return data;
          }
          markSource(serviceName, 'live');
          return data;
        }

        if (diagnostic?.status !== 'ok') {
          console.warn(`[Dashboard] ${serviceName} unavailable:`, diagnostic);
        } else {
          console.warn(`[Dashboard] ${serviceName} unavailable (empty or invalid response)`);
        }

        if (USE_DASHBOARD_MOCK) {
          markSource(serviceName, 'mock');
          console.log(`[Dashboard] ${serviceName} using demo data (REACT_APP_DASHBOARD_USE_MOCK=true)`);
          return getMockFallback(serviceName);
        }

        if (sourceKey) {
          sources[sourceKey] = 'unavailable';
        }
        return null;
      };

      const normalizeEmptyArKpi = (kpiName, raw) => {
        if (!raw || typeof raw !== 'object') return raw;
        if (kpiName === 'daysInAR' && (raw.total_charges_90d ?? 0) === 0 && (raw.outstanding_ar ?? 0) === 0) {
          return { ...raw, days_in_ar: null };
        }
        if (kpiName === 'netCollectionRate' && (raw.data_status === 'insufficient_charges' || (raw.net_charges ?? 0) === 0)) {
          return { ...raw, net_collection_rate: null };
        }
        if (kpiName === 'posCollections' && (raw.total_patient_collections ?? 0) === 0) {
          return { ...raw, pos_collections: null, pos_percentage: null };
        }
        if (kpiName === 'propensityToPay' && (raw.total_patients ?? 0) === 0) {
          return { ...raw, avg_propensity_score: null };
        }
        if (kpiName === 'costToCollect' && (raw.total_collections ?? 0) === 0) {
          return { ...raw, cost_to_collect_pct: null };
        }
        return raw;
      };

      const extractKPI = (result, kpiName) => {
        const metaKey = `kpis.${kpiName}`;
        const { data, diagnostic } = getPayload(result);

        if (data != null && typeof data === 'object' && !Array.isArray(data)) {
          let raw = data;
          if (raw.data != null && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
            raw = { ...raw, ...raw.data };
          }
          raw = normalizeEmptyArKpi(kpiName, raw);
          const hasValue = kpiName === 'daysInAR' ? raw.days_in_ar != null
            : kpiName === 'netCollectionRate' ? raw.net_collection_rate != null
            : kpiName === 'posCollections' ? raw.pos_collections != null
            : kpiName === 'propensityToPay' ? raw.avg_propensity_score != null
            : kpiName === 'costToCollect' ? raw.cost_to_collect_pct != null
            : true;
          if (!hasValue) {
            sources[metaKey] = 'unavailable';
            return null;
          }
          sources[metaKey] = 'live';
          return raw;
        }

        if (diagnostic?.status !== 'ok') {
          console.warn(`[Dashboard] KPI ${kpiName} unavailable:`, diagnostic);
        }

        if (USE_DASHBOARD_MOCK) {
          sources[metaKey] = 'mock';
          console.log(`[Dashboard] KPI ${kpiName} using demo data (REACT_APP_DASHBOARD_USE_MOCK=true)`);
          return rcmService.getMockKPIData(kpiName);
        }

        sources[metaKey] = 'unavailable';
        return null;
      };

      const calculateAutomationRate = () => {
        const queues = [
          denialWorkQueue, claimsWorkQueue, chargeWorkQueue,
          insuranceWorkQueue, priorAuthWorkQueue, arPostingWorkQueue
        ];

        let totalItems = 0;
        let aiAssigned = 0;
        let hasLiveQueueData = false;

        queues.forEach((queue) => {
          const { data } = getPayload(queue);
          if (data) {
            hasLiveQueueData = true;
            const normalized = rcmService.ensureWorkQueueStructure(data);
            totalItems += (normalized?.total_items || 0);
            aiAssigned += (normalized?.assigned_to_ai || 0);
          }
        });

        if (hasLiveQueueData) {
          sources['kpis.automationRate'] = 'live';
          return totalItems > 0 ? (aiAssigned / totalItems * 100) : 0;
        }

        if (USE_DASHBOARD_MOCK) {
          sources['kpis.automationRate'] = 'mock';
          return rcmService.getMockKPIData('automationRate');
        }

        sources['kpis.automationRate'] = 'unavailable';
        return null;
      };

      const kpiDaysInAR = extractKPI(daysInAR, 'daysInAR');
      const kpiNetCollectionRate = extractKPI(netCollectionRate, 'netCollectionRate');
      const kpiPosCollections = extractKPI(posCollections, 'posCollections');
      const kpiPropensityToPay = extractKPI(propensityToPay, 'propensityToPay');
      const kpiCostToCollect = extractKPI(costToCollect, 'costToCollect');
      const kpiAutomationRate = calculateAutomationRate();

      const arPostingRaw = extractStats(arPostingStats, 'ARPosting');
      const arReconciliationRaw = getPayload(arReconciliationStats).data;
      const arPostingNormalized = arPostingRaw
        ? normalizeArPostingDashboardStats(arPostingRaw, arReconciliationRaw)
        : null;

      const denialsRaw = extractStats(denialStats, 'DenialManagement');
      const claimsRaw = extractStats(claimsStats, 'ClaimsProcessing');
      const claimsWorkQueueRaw = extractStats(claimsWorkQueue, 'ClaimsProcessing WorkQueue');

      const hasAnyKpi = [kpiDaysInAR, kpiNetCollectionRate, kpiPosCollections, kpiPropensityToPay, kpiCostToCollect, kpiAutomationRate]
        .some((kpi) => kpi != null);

      const dashboardData = {
        // Executive KPIs
        denials: normalizeDenialsDashboardStats(denialsRaw),
        claims: normalizeClaimsDashboardStats(claimsRaw, claimsWorkQueueRaw),
        charges: extractStats(chargeStats, 'ChargeCapture'),
        insurance: extractStats(insuranceStats, 'InsuranceVerification'),
        priorAuth: extractStats(priorAuthStats, 'PriorAuthorization'),
        arPosting: arPostingNormalized,
        clearinghouse: extractStats(clearinghouseStats, 'ClearingHouse'),
        // Operational KPIs - Work Queue status
        denialsWorkQueue: extractStats(denialWorkQueue, 'DenialManagement WorkQueue'),
        claimsWorkQueue: claimsWorkQueueRaw,
        chargesWorkQueue: extractStats(chargeWorkQueue, 'ChargeCapture WorkQueue'),
        insuranceWorkQueue: extractStats(insuranceWorkQueue, 'InsuranceVerification WorkQueue'),
        priorAuthWorkQueue: extractStats(priorAuthWorkQueue, 'PriorAuthorization WorkQueue'),
        arPostingWorkQueue: extractStats(arPostingWorkQueue, 'ARPosting WorkQueue'),
        kpis: hasAnyKpi ? {
          daysInAR: kpiDaysInAR,
          netCollectionRate: kpiNetCollectionRate,
          posCollections: kpiPosCollections,
          propensityToPay: kpiPropensityToPay,
          costToCollect: kpiCostToCollect,
          automationRate: kpiAutomationRate,
        } : null,
        meta: {
          ...summarizeSources(sources),
          sources,
          diagnostics,
          mockEnabled: USE_DASHBOARD_MOCK,
        },
        loadTime,
        generatedAt: new Date().toISOString()
      };

      console.log('[Dashboard] Executive summary KPIs prepared:', {
        hasDenials: !!dashboardData.denials,
        hasClaims: !!dashboardData.claims,
        hasCharges: !!dashboardData.charges,
        hasInsurance: !!dashboardData.insurance,
        hasPriorAuth: !!dashboardData.priorAuth,
        hasARPosting: !!dashboardData.arPosting,
        hasClearinghouse: !!dashboardData.clearinghouse,
        loadTime: `${loadTime}ms`
      });

      return dashboardData;
    } catch (error) {
      console.error('[Dashboard] Error fetching dashboard data:', error);
      // Return empty data instead of throwing to allow dashboard to render
      return this.getEmptyDashboardData('error');
    }
  },

  // Helper to validate data has meaningful values
  isValidData(data, serviceName) {
    if (!data || typeof data !== 'object') return false;
    
    // Check for common zero/invalid patterns
    const keys = Object.keys(data);
    if (keys.length === 0) return false;
    
    // For WorkQueue data, check specific fields
    if (serviceName.includes('WorkQueue')) {
      const hasNonZeroValue = 
        (data.total_items && data.total_items > 0) ||
        (data.pending_count && data.pending_count > 0) ||
        (data.in_progress_count && data.in_progress_count > 0) ||
        (data.completed_today && data.completed_today > 0) ||
        (data.pending && data.pending > 0) ||
        (data.in_progress && data.in_progress > 0) ||
        (data.completed && data.completed > 0) ||
        (data.assigned_to_ai && data.assigned_to_ai > 0) ||
        (data.assigned_to_human && data.assigned_to_human > 0);
      
      // If all work queue values are zero or missing, consider invalid
      if (!hasNonZeroValue) {
        return false;
      }
    }
    
    // Check if all numeric values are zero
    const numericValues = keys
      .map(key => {
        const value = data[key];
        if (typeof value === 'number') return value;
        if (typeof value === 'object' && value !== null) {
          // Check nested objects recursively
          const nestedValues = Object.values(value).filter(v => typeof v === 'number');
          return nestedValues.length > 0 ? nestedValues : null;
        }
        return null;
      })
      .filter(v => v !== null)
      .flat();
    
    // If we have numeric values and they're all zero, data is invalid
    if (numericValues.length > 0 && numericValues.every(v => v === 0)) {
      return false;
    }
    
    // If no numeric values found at all, consider invalid
    if (numericValues.length === 0) {
      return false;
    }
    
    return true;
  },

  // Get mock data for a service
  getMockData(serviceName) {
    const mockData = {
      'DenialManagement': {
        denial_summary: {
          total_denials: 85,
          total_claims: 600,
          denial_rate: 14.1
        },
        financial_summary: {
          total_denied: 125000,
          total_recovered: 75000,
          pending_amount: 35000,
          lost_amount: 15000
        },
        workflow_summary: {
          pending_appeals: 45,
          avg_appeal_time: 12,
          successful_appeals: 95,
          failed_appeals: 25
        },
        performance_metrics: {
          appeal_success_rate: 79.2,
        }
      },
      'DenialManagement WorkQueue': {
        total_items: 85,
        pending_count: 25,
        in_progress_count: 15,
        completed_today: 8,
        assigned_to_ai: 35,
        assigned_to_human: 50,
        overdue_count: 5,
        avg_processing_time: 2.5
      },
      'ClaimsProcessing': {
        recent_claims_30_days: 1180,
        financial_summary: {
          total_charged: 2850000,
          total_paid: 2450000,
        },
        processing_summary: {
          total_processed: 1180,
          success_rate: 94.4,
          automated_processing: 890,
        },
        clean_claim_rate: 94.4,
        success_rate: 94.4,
      },
      'ClaimsProcessing WorkQueue': {
        total_items: 1250,
        pending_count: 45,
        in_progress_count: 120,
        completed_today: 85,
        assigned_to_ai: 890,
        assigned_to_human: 360,
        overdue_count: 8,
        avg_processing_time: 1.8
      },
      'ChargeCapture': {
        summary: {
          total_charges: 1850,
          posted_charges: 1720,
          pending_charges: 95,
          rejected_charges: 35,
          charge_capture_rate: 93.0,
          total_revenue: 2850000,
          average_charge_amount: 1543,
          missing_encounters: 12
        }
      },
      'ChargeCapture WorkQueue': {
        total_items: 1850,
        pending_count: 95,
        in_progress_count: 85,
        completed_today: 120,
        assigned_to_ai: 1100,
        assigned_to_human: 750,
        overdue_count: 12,
        avg_processing_time: 1.2
      },
      'InsuranceVerification': {
        verification_success_rate: {
          periods: {
            '30d': {
              total_verifications: 2100,
              successful_verifications: 1980,
              success_rate: 94.3
            }
          }
        },
        work_queue_status: {
          pending_count: 85
        },
        patient_responsibility_collection: {
          periods: {
            mtd: {
              total_collected: 125000
            }
          }
        }
      },
      'InsuranceVerification WorkQueue': {
        total_items: 2100,
        pending_count: 85,
        in_progress_count: 65,
        completed_today: 145,
        assigned_to_ai: 1650,
        assigned_to_human: 450,
        overdue_count: 5,
        avg_processing_time: 0.8
      },
      'PriorAuthorization': {
        periods: {
          '30d': {
            total_requests: 320,
            approved_requests: 285,
            pending_requests: 25,
            denied_requests: 10,
            approval_rate: 89.1,
            revenue_protected: 450000,
            average_days_to_authorization: 3.5
          }
        }
      },
      'PriorAuthorization WorkQueue': {
        total_items: 320,
        pending_count: 25,
        in_progress_count: 18,
        completed_today: 12,
        assigned_to_ai: 195,
        assigned_to_human: 125,
        overdue_count: 3,
        avg_processing_time: 3.5
      },
      'ARPosting': {
        summary: {
          total_postings: 1450,
          total_posted: 1450,
          total_posted_today: 85,
          successful_postings: 1380,
          pending_postings: 55,
          failed_postings: 15,
          auto_posting_rate: 95.2,
          reconciliation_rate: 92.5,
          pending_actions: 55,
          total_amount_posted: 1850000
        }
      },
      'ARPosting WorkQueue': {
        total_items: 1450,
        pending_count: 55,
        in_progress_count: 75,
        completed_today: 42,
        assigned_to_ai: 950,
        assigned_to_human: 500,
        overdue_count: 8,
        avg_processing_time: 1.5
      },
      'ClearingHouse': {
        total_transactions: 8500,
        successful_transactions: 8250,
        pending_transactions: 180,
        failed_transactions: 70,
        success_rate: 97.1
      }
    };
    
    return mockData[serviceName] || null;
  },

  // Get enhanced mock KPI data for better demos
  getMockKPIData(kpiName) {
    const mockKPIs = {
      daysInAR: {
        days_in_ar: 32.5,
        target: 40.0,
        trend: 'improving', // improving, stable, declining
        previous_period: 38.2,
        industry_average: 45.0,
        explanation: 'Days in A/R measures the average number of days it takes to collect payment. Lower is better.'
      },
      netCollectionRate: {
        net_collection_rate: 97.2,
        target: 95.0,
        trend: 'excellent',
        previous_period: 96.1,
        industry_average: 93.5,
        explanation: 'Net Collection Rate is the percentage of allowed charges that are actually collected. Higher is better.'
      },
      posCollections: {
        pos_collections: 18750.0,
        pos_percentage: 62.5,
        target: 60.0,
        trend: 'exceeding',
        previous_period: 58.3,
        total_patient_payments: 30000.0,
        explanation: 'Point of Service Collections measures payments collected at the time of service. Higher percentage reduces A/R.'
      },
      propensityToPay: {
        avg_propensity_score: 72.5,
        high_risk_patients: 15,
        medium_risk_patients: 35,
        low_risk_patients: 50,
        trend: 'improving',
        previous_period: 68.2,
        explanation: 'Propensity to Pay score predicts patient payment likelihood (0-100). Higher scores indicate better payment probability.'
      },
      costToCollect: {
        cost_to_collect_pct: 2.1,
        target: 3.0,
        total_collection_costs: 12500.0,
        total_collections: 595238.0,
        trend: 'efficient',
        previous_period: 2.6,
        industry_average: 3.5,
        explanation: 'Cost to Collect is the percentage of revenue spent on collection activities. Lower is more efficient.'
      },
      automationRate: 78.5
    };
    
    return mockKPIs[kpiName] || null;
  },

  // Ensure work queue data has consistent structure
  ensureWorkQueueStructure(data) {
    if (!data || typeof data !== 'object') {
      return null;
    }

    if (data.pending_claims != null || (data.total_claims != null && data.status_distribution != null)) {
      return normalizeClaimsWorkQueueStructure(data);
    }

    return {
      total_items: data.total_items || data.totalItems || data.total_pending || data.total_claims || 0,
      pending_count:
        data.pending_count
        || data.pending
        || data.pendingItems
        || data.pending_claims
        || data.total_pending
        || 0,
      in_progress_count: data.in_progress_count || data.in_progress || data.inProgressItems || 0,
      completed_today: data.completed_today || data.completed || data.completedToday || 0,
      assigned_to_ai: data.assigned_to_ai || data.assignedToAI || 0,
      assigned_to_human: data.assigned_to_human || data.assignedToHuman || 0,
      overdue_count:
        data.overdue_count
        || data.overdue_items
        || data.overdueItems
        || data.approaching_timely_filing
        || data.approaching_deadline_count
        || 0,
      avg_processing_time:
        data.avg_processing_time
        || data.avgProcessingTime
        || data.average_age_days
        || data.avg_age_days
        || 0,
    };
  },

  // Get default work queue data structure if mock data is missing
  getDefaultWorkQueueData() {
    return {
      total_items: 100,
      pending_count: 15,
      in_progress_count: 20,
      completed_today: 10,
      assigned_to_ai: 60,
      assigned_to_human: 40,
      overdue_count: 3,
      avg_processing_time: 2.0
    };
  },

  // Helper to return empty dashboard data structure (no demo substitution)
  getEmptyDashboardData(reason = 'unavailable') {
    if (USE_DASHBOARD_MOCK) {
      const sources = createEmptySources();
      Object.keys(sources).forEach((key) => {
        sources[key] = 'mock';
      });
      return {
        denials: this.getMockData('DenialManagement'),
        claims: this.getMockData('ClaimsProcessing'),
        charges: this.getMockData('ChargeCapture'),
        insurance: this.getMockData('InsuranceVerification'),
        priorAuth: this.getMockData('PriorAuthorization'),
        arPosting: this.getMockData('ARPosting'),
        clearinghouse: this.getMockData('ClearingHouse'),
        denialsWorkQueue: this.ensureWorkQueueStructure(this.getMockData('DenialManagement WorkQueue')),
        claimsWorkQueue: this.ensureWorkQueueStructure(this.getMockData('ClaimsProcessing WorkQueue')),
        chargesWorkQueue: this.ensureWorkQueueStructure(this.getMockData('ChargeCapture WorkQueue')),
        insuranceWorkQueue: this.ensureWorkQueueStructure(this.getMockData('InsuranceVerification WorkQueue')),
        priorAuthWorkQueue: this.ensureWorkQueueStructure(this.getMockData('PriorAuthorization WorkQueue')),
        arPostingWorkQueue: this.ensureWorkQueueStructure(this.getMockData('ARPosting WorkQueue')),
        kpis: {
          daysInAR: this.getMockKPIData('daysInAR'),
          netCollectionRate: this.getMockKPIData('netCollectionRate'),
          posCollections: this.getMockKPIData('posCollections'),
          propensityToPay: this.getMockKPIData('propensityToPay'),
          costToCollect: this.getMockKPIData('costToCollect'),
          automationRate: this.getMockKPIData('automationRate'),
        },
        meta: {
          ...summarizeSources(sources),
          sources,
          mockEnabled: true,
          reason,
        },
        loadTime: 0,
        generatedAt: new Date().toISOString(),
      };
    }

    const sources = createEmptySources();
    return {
      denials: null,
      claims: null,
      charges: null,
      insurance: null,
      priorAuth: null,
      arPosting: null,
      clearinghouse: null,
      denialsWorkQueue: null,
      claimsWorkQueue: null,
      chargesWorkQueue: null,
      insuranceWorkQueue: null,
      priorAuthWorkQueue: null,
      arPostingWorkQueue: null,
      kpis: null,
      meta: {
        ...summarizeSources(sources),
        sources,
        mockEnabled: false,
        reason,
      },
      loadTime: 0,
      generatedAt: new Date().toISOString(),
    };
  },

  // Analytics data
  async getAnalyticsData() {
    try {
      // Get organization_id (synchronous)
      const organizationId = getOrganizationId();
      
      if (!organizationId) {
        console.warn('No organization_id available for analytics');
        return {
          denials: {},
          claims: {},
          charges: {},
        };
      }
      
      const [denialsAnalytics, claimsAnalytics, chargesAnalytics] = await Promise.all([
        denialService.get('/api/v1/analytics', { params: { organization_id: organizationId } }).catch(() => ({})),
        claimsService.get('/api/v1/analytics', { params: { organization_id: organizationId } }).catch(() => ({})),
        chargeService
          ? chargeService.get('/api/v1/analytics', { params: { organization_id: organizationId } }).catch(() => ({}))
          : Promise.resolve({}),
      ]);

      return {
        denials: denialsAnalytics,
        claims: claimsAnalytics,
        charges: chargesAnalytics,
      };
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      return {};
    }
  },
};

